import React, { useState, useEffect } from 'react';
import { Row, Card, Divider, message, Tag, Typography, Col } from 'antd';
import _commonService from '../../modules/commonService';
import { COMMON_ERROR_MSG } from '../../constants/constants';
import _NewapplicationService from '../../modules/NewapplicationService';
import _ApplicantBankerAdjustmentService from '../../modules/ApplicantBankerAdjustmentService';
import { QuestionCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useHistory } from "react-router-dom";
import { routes } from '../../routes/Routes';
import CommentSection from './CommentSection';
import ReviewCommentByApplicationIdAndSection from '../NewApplication/ReviewCommentByApplicationIdAndSection';
import AdjustmentField from '../../components/AdjustmentField/AdjustmentField';
import { AdjustmentScreenModule, AdjustmentTableName } from '../../constants/adjustmentConstants';
import { canViewAdjustments, canAdjust } from '../../utils/adjustmentPermissions';
const { Title, Text, Paragraph } = Typography;

// Balance Sheet - Questionnaire section (BankerAdjustmentExecutionPlan.md Section 6.7) -- missed
// in the original build. Only "ConditionalLiability" questions carry an adjustable Amount; this
// matches ApplicantService's own net worth formula, which subtracts totalQuestionsAmount
// alongside LiabilitiesTotal (i.e. it represents contingent liabilities, not assets).
const ADJUSTABLE_QUESTION_TYPE = "ConditionalLiability";


const BalanceSheetQuestionnaireDashboard = ({ activeKey, isUsedInBanker, applicationstatusid,applicantDetails, ApplicationObjId }) => {
    const LoanOfficerId = localStorage.getItem("LoanOfficerId");
    const ApplicationObjID = localStorage.getItem("ApplicationObjId");
    const BankerId = localStorage.getItem("BankerId");
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [LoanOfficerID] = useState(LoanOfficerId);
    const [BankerID] = useState(BankerId);
    const history = useHistory();

    // Banker/Underwriter Adjustment feature: populated for Senior Private Banker/Underwriter
    // Banker (view + edit) and, as of the view-only extension, also for Loan Officer/Compliance
    // Administrator/Wealth Advisor (view only, via canAdjust staying false for them) when viewing
    // this dashboard from the Banker-side screen -- every other role's render path below is
    // unchanged. `isUsedInBanker` is still required to gate on, since this same component (with
    // isUsedInBanker=false) is also reached elsewhere outside the Banker-side FinancialStatementTab.js.
    const [adjustments, setAdjustments] = useState([]);
    const canSeeAdjustments = isUsedInBanker && canViewAdjustments();
    const isAdjustmentsLocked = adjustments.some((a) => a.IsLocked);
    const canEditAdjustments = canSeeAdjustments && canAdjust(applicationstatusid, isAdjustmentsLocked);

    const applicationId = localStorage.getItem("Id");
    const effectiveApplicationId = isUsedInBanker ? ApplicationObjId : applicationId;

    useEffect(() => {
        fetchQuestions();
        fetchAnswersByApplicationId();
        if (canSeeAdjustments) {
            fetchAdjustments();
        }
    }, []);

    const fetchAdjustments = async () => {
        try {
            const res = await _ApplicantBankerAdjustmentService.getAdjustmentsByApplicationId(effectiveApplicationId);
            if (res.data && res.data.IsSuccess) {
                setAdjustments(res.data.Obj || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const findAdjustment = (tableName, fieldName, sourceRowId = null) =>
        adjustments.find(
            (a) => a.TableName === tableName && a.FieldName === fieldName && (a.SourceRowId ?? null) === sourceRowId
        );

    const handleSaveAdjustment = async (screenModule, tableName, fieldName, sourceRowId, adjustedValue, reason) => {
        const res = await _ApplicantBankerAdjustmentService.saveAdjustment({
            applicationId: effectiveApplicationId,
            screenModule,
            tableName,
            fieldName,
            sourceRowId,
            adjustedValue,
            reason,
        });
        if (res.data && res.data.IsSuccess) {
            message.success(res.data.Message || "Adjustment saved successfully.");
            await fetchAdjustments();
        } else {
            message.error((res.data && res.data.Message) || COMMON_ERROR_MSG);
            throw new Error((res.data && res.data.Message) || "Save failed");
        }
    };

    const handleViewHistory = async (tableName, fieldName, sourceRowId) => {
        const res = await _ApplicantBankerAdjustmentService.getAdjustmentHistory({
            applicationId: effectiveApplicationId,
            tableName,
            fieldName,
            sourceRowId,
        });
        if (res.data && res.data.IsSuccess) {
            return res.data.Obj || [];
        }
        message.error((res.data && res.data.Message) || "Failed to load adjustment history.");
        return [];
    };

    const fetchQuestions = async () => {
        try {
            const res = await _NewapplicationService.getAllQuestions();
            if (res.data?.IsSuccess && Array.isArray(res.data.Obj)) {
                setQuestions(res.data.Obj);
            } else {
                message.error(res.data.Message || COMMON_ERROR_MSG);
            }
        } catch (error) {
            console.error(error);
        } finally {
            //   setLoading(false);
        }
    };

    const fetchAnswersByApplicationId = async () => {
        try {
            const res = await _NewapplicationService.getQuestionsByApplicationID(isUsedInBanker ? ApplicationObjId : applicationId);
            if (Array.isArray(res.data) && res.data.length > 0) {
                setAnswers(res.data);
            } else {
                message.error(res.data.Message || COMMON_ERROR_MSG);
            }
        } catch (error) {
            console.error(error);
        } finally {
            // setLoading(false);
        }
    };


    // Group questions by their QuestionType
    const groupedQuestions = questions.reduce((acc, question) => {
        if (!acc[question.QuestionType]) {
            acc[question.QuestionType] = [];
        }
        acc[question.QuestionType].push(question);
        return acc;
    }, {});

    const balanceSheetQuestionnaireEditClick = () => {
        history.push({
            pathname: routes.questionnaire.path,
            state: { applicationObjId: localStorage.getItem("ApplicationObjId"), isFromDashboard: true, activeKey: activeKey },
        });
    }

    return (
        <Card
            title={
                <Row justify="space-between" align="middle" className="balance-heading">
                    <Text strong>
                        <span role="img" aria-label="user" style={{ marginRight: 8 }}><QuestionCircleOutlined /> </span>
                        Balance Sheet – Questionnaire
                    </Text>
                    {!isUsedInBanker &&
                        <EditOutlined onClick={balanceSheetQuestionnaireEditClick} style={{ color: "#595959", cursor: "pointer" }} />
                    }
                </Row>
            }
            // style={{
            //     borderRadius: 16,
            //     boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
            // }}
            className="mb30 card-section custom-section"

        >
            <div>

                {Object.entries(groupedQuestions).map(([section, questionsInSection]) => (

                    <div key={section}>
                        <Typography.Title level={5}>{section == "Asset" ? "Assets:" : "Contingent Liabilities:"}</Typography.Title>

                        {questionsInSection.map((question) => {
                            const answerRecord = answers.find(
                                (ans) => ans.QuestionId === question.Id
                            );

                            // Default values if answer not found
                            const answerExists = !!answerRecord;
                            const isYes = answerExists ? answerRecord.Answer : false;
                            const amount = answerRecord?.Amount;
                            const description = answerRecord?.Description;

                            return (
                                <div key={question.Id}>
                                    <Row gutter={16} justify="space-between">
                                        <Col span={isUsedInBanker ? 22 : 16}>
                                            <Paragraph style={{ marginBottom: 10 }}>
                                                {question.QuestionText}
                                            </Paragraph>
                                        </Col>

                                        <Col span={isUsedInBanker ? 2 : 3}>
                                            <Tag
                                                color={isYes ? 'green' : 'red'}
                                                style={{ marginLeft: 32 }}
                                            >
                                                {isYes ? 'Yes' : 'No'}
                                            </Tag>
                                        </Col>
                                    </Row>

                                    {isYes && question.Is_Descriptive && (
                                        <>
                                            {canSeeAdjustments && question.QuestionType === ADJUSTABLE_QUESTION_TYPE ? (
                                                <div style={{ marginBottom: 10 }}>
                                                    <AdjustmentField
                                                        label="Amount"
                                                        asSubmittedValue={amount}
                                                        adjustment={findAdjustment(AdjustmentTableName.ApplicantQuestionnaire, "Amount", answerRecord?.Id)}
                                                        canEdit={canEditAdjustments}
                                                        onSave={(value, reason) =>
                                                            handleSaveAdjustment(AdjustmentScreenModule.BalanceSheetQuestionnaire, AdjustmentTableName.ApplicantQuestionnaire, "Amount", answerRecord?.Id, value, reason)
                                                        }
                                                        onViewHistory={() => handleViewHistory(AdjustmentTableName.ApplicantQuestionnaire, "Amount", answerRecord?.Id)}
                                                    />
                                                </div>
                                            ) : (
                                                <Paragraph style={{ marginBottom: 10 }}>
                                                    <strong>Amount:</strong> $ {_commonService.formatNumberWithCommas(amount) || 'N/A'}
                                                </Paragraph>
                                            )}
                                            <Paragraph style={{ marginBottom: 10 }}>
                                                <strong>Comments:</strong> {description || 'N/A'}
                                            </Paragraph>
                                        </>
                                    )}

                                    <Divider style={{ margin: "10px 0" }} />
                                </div>
                            );
                        })}
                    </div>

                ))}

                {
                    (!ApplicationObjID && (LoanOfficerID || BankerID)) && <CommentSection CommentSectionName="questionair" applicationstatusid={applicationstatusid} applicantDetails={applicantDetails} ApplicationObjId={ApplicationObjId} />
                }
                {
                    (ApplicationObjID) && <ReviewCommentByApplicationIdAndSection CommentSectionName="questionair" />
                }

            

            </div>
        </Card>
    );
};

export default BalanceSheetQuestionnaireDashboard;